import type { JSX } from 'solid-js';
import { For, Show } from 'solid-js';
import { t } from '../i18n';
import { prDecideCall } from '../lib/pr-detail';
import type { ScopeVerdict } from '../lib/scope-gate';
import { CopyAsCall } from './CopyAsCall';
import { Button } from './ui/Button';

// "The page that proves trust." Lists ask_before_acting items as hairline rows
// (mock RepoDetail → Pending): drafted action + mono ref left, controls right.
// Approve/skip mutations POST to apps/api; Edit opens the <ActionDrawer/>.
//
// The row also names the change (#1396): a decision screen that showed only
// `repo#88` said less about the PR than the Home card that linked to it — you
// approved a number without being told what it was called or who wrote it.
//
// A decision is OF the head the row showed (plan 101 M2): the callbacks carry
// it, and a row with no known head holds Approve/Skip and says why.
// Both are optional: an unknown title/author drops its line rather than
// rendering an empty one.
//
// `mayDecide: 'denied'` (`write:prs` missing from `/v1/me`, #7426) drops
// Approve / Skip and their copy-as-call — both only ever answer 403 — and says
// so once, above the list. Edit (the drafted action's context) stays.

export type PendingRow = {
  readonly id: string;
  readonly repo_pr: string;
  readonly drafted_action: string;
  /** The PR's own title — `''`/absent when the projection never cached one. */
  readonly subject?: string;
  readonly author?: string | null;
  /** The PR head this row shows — what a decision is OF (plan 101 M2). Null: nothing to decide yet. */
  readonly head_sha: string | null;
};

/** "acme/widgets#88 · first-timer" — drops the author when unknown. */
function ref(row: PendingRow): string {
  return [row.repo_pr, row.author].filter((p): p is string => !!p).join(' · ');
}

export type PendingTableProps = {
  readonly rows: readonly PendingRow[];
  readonly busy?: boolean;
  /** `write:prs` per `/v1/me`; unsettled holds the decision buttons. */
  readonly mayDecide: ScopeVerdict;
  /** `headSha` is the head the row showed; never fired for a row without one. */
  readonly onApprove?: (id: string, headSha: string) => void;
  readonly onSkip?: (id: string, headSha: string) => void;
  readonly onEdit?: (id: string) => void;
};

export function PendingTable(props: PendingTableProps): JSX.Element {
  const deciding = (): boolean => props.mayDecide !== 'denied';
  const held = (row: PendingRow): boolean =>
    !!props.busy || props.mayDecide !== 'allowed' || row.head_sha === null;
  return (
    <>
      <Show when={props.mayDecide === 'denied'}>
        <p class="pending-list__hint" role="status">
          {t('approvals.decide_denied')}
        </p>
      </Show>
      <ul class="pending-list">
        <For each={props.rows}>
          {(row) => (
            <li class="pending-list__row" data-id={row.id}>
              <div class="pending-list__lead">
                <p class="pending-list__title">{row.drafted_action}</p>
                <Show when={row.subject}>
                  <p class="pending-list__subject">{row.subject}</p>
                </Show>
                <p class="pending-list__ref">{ref(row)}</p>
                <Show when={row.head_sha === null}>
                  <p class="pending-list__hint">{t('approvals.head_unknown')}</p>
                </Show>
              </div>
              {/* The visible label stays the one-word verb — the column is read
                down, and the row above it names the change. The ACCESSIBLE name
                carries the ref: nine rows meant nine buttons named exactly
                "Approve" with the `repo_pr` in a sibling <p>, which is not an
                accessible name, so a screen-reader user tabbing the list
                approved a number they were never told. Axe does not flag
                duplicate names, which is why the sweep passed on it. */}
              <div class="pending-list__controls">
                <Show when={deciding()}>
                  <Button
                    size="sm"
                    data-action="approve"
                    aria-label={t('approvals.approve_named', { ref: ref(row) })}
                    disabled={held(row)}
                    onClick={() => {
                      if (row.head_sha !== null) props.onApprove?.(row.id, row.head_sha);
                    }}
                  >
                    {t('approvals.approve')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-action="skip"
                    aria-label={t('approvals.skip_named', { ref: ref(row) })}
                    disabled={held(row)}
                    onClick={() => {
                      if (row.head_sha !== null) props.onSkip?.(row.id, row.head_sha);
                    }}
                  >
                    {t('approvals.skip')}
                  </Button>
                </Show>
                <Button
                  variant="ghost"
                  size="sm"
                  data-action="edit"
                  aria-label={t('approvals.edit_named', { ref: ref(row) })}
                  disabled={props.busy}
                  onClick={() => props.onEdit?.(row.id)}
                >
                  {t('approvals.edit')}
                </Button>
                {/* Both forks in ONE disclosure: they are two directions of one
                  decision, and a reader comparing them is the point. */}
                <Show when={deciding() && row.head_sha} keyed>
                  {(head) => (
                    <CopyAsCall
                      calls={[
                        {
                          label: t('approvals.approve'),
                          call: prDecideCall(row.id, 'approve', head),
                        },
                        { label: t('approvals.skip'), call: prDecideCall(row.id, 'skip', head) },
                      ]}
                    />
                  )}
                </Show>
              </div>
            </li>
          )}
        </For>
      </ul>
    </>
  );
}
